import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import { Alert, ConfirmDialog, EmptyState, ExportButton, FormActions, FormDateInput, FormField, FormSelect, FormTextarea, PageHeader, StatusBadge, TableActions } from '../components/ui';
import { ExpandChevron, ExpandableRow } from '../components/ui/ExpandableRow';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import useFetchWithRefresh from '../utils/useFetchWithRefresh';
import { ExcelColumn } from '../utils/exportToExcel';

// Updated Vineyard interface based on /api/batch nested vineyard
interface Vineyard {
  id: number;
  name: string;
  location?: string; // Added
  planted_variety?: string; // Added, snake_case
  planting_date?: string; // Added, snake_case
  acreage?: string; // Added, string from JSON
  brix?: string;    // Added, string from JSON
  ph?: string;      // Added, string from JSON
  ta?: string;      // Added, string from JSON
  sg?: string;      // Added, string from JSON
  notes?: string;   // Added
  createdAt?: string; // Added, camelCase
  updatedAt?: string; // Added, camelCase
}

// New Winemaker interface based on /api/batch nested winemaker
interface Winemaker {
  id: number;
  first_name?: string; // snake_case
  last_name?: string;  // snake_case
  email?: string;
  // password excluded
  role_id?: number;    // snake_case
  active?: boolean;
  last_login?: string; // snake_case
  createdAt?: string; // camelCase
  updatedAt?: string; // camelCase
}

// Updated WineBatch interface based on /api/batch response
interface WineBatch {
  id: number;
  batch_id: string;
  vineyard_id?: number | null; // Optional/nullable
  batch_name: string;
  wine_type?: string;
  grape_variety?: string; // Added
  start_date?: string;
  end_date?: string | null; // Added
  status: string;
  weight?: string; // Added, string from JSON
  net_liquid_weight?: string; // Added, string from JSON
  final_brix?: string; // Added, string from JSON
  final_ph?: string;   // Added, string from JSON
  final_ta?: string;   // Added, string from JSON
  final_sg?: string;   // Added, string from JSON

  responsible_user_id?: number; // Added
  notes?: string;
  createdAt: string; // Added, camelCase
  updatedAt: string; // Added, camelCase
  vineyard?: Vineyard; // Added nested Vineyard
  winemaker?: Winemaker; // Added nested Winemaker
  fermentations?: Fermentation[]; // Added fermentations array
  // vesselId removed, assuming vessel info comes from other endpoints or is handled differently
}

// Adding Fermentation interface for related data
interface Fermentation {
  id: number;
  batch_id: number;
  vessel_id: number;
  start_date: string;
  end_date?: string | null;
  initial_brix?: string;
  status: string;
  press_date?: string | null;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  vessel?: Vessel;
}

// Adding Vessel interface for related data
interface Vessel {
  id: number;
  vessel_id: string;
  type?: string;
  capacity?: string;
  capacity_unit?: string;
  material?: string;
  location?: string;
  vintage?: number;
  toast?: string;
  cooperage?: string;
  usage_type?: string;
  status?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Form values - adjust based on actual form fields
interface WineBatchFormValues {
  batch_name: string;
  batch_id: string;
  vineyard_id?: number; // Added if selectable in form
  responsible_user_id?: number; // Added if selectable in form
  wine_type?: string;
  start_date?: string;
  end_date?: string | null;
  status?: string;
  notes?: string;
  // Measurement values
  weight?: number;
  net_liquid_weight?: number;
  final_brix?: number;
  final_ph?: number;
  final_ta?: number;
  final_sg?: number;
}

// Adjust Yup schema based on actual form requirements
const WineBatchSchema = Yup.object().shape({
  batch_name: Yup.string().required('Batch name is required'),
  batch_id: Yup.string().required('Batch ID is required'),
  vineyard_id: Yup.number().nullable(), // Added if in form
  responsible_user_id: Yup.number().nullable(), // Added if in form
  wine_type: Yup.string().required('Wine type is required'),
  start_date: Yup.date().required('Start date is required').typeError('Valid start date is required'),
  end_date: Yup.date().nullable(),
  status: Yup.string(),
  notes: Yup.string(),
  // Measurement fields validation
  weight: Yup.number().nullable().positive('Weight must be positive'),
  net_liquid_weight: Yup.number().nullable().positive('Net liquid weight must be positive'),
  final_brix: Yup.number().nullable(),
  final_ph: Yup.number().nullable(),
  final_ta: Yup.number().nullable(),
  final_sg: Yup.number().nullable()
});

const statusOptions = [
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' }
];


const wineTypes = [
  { value: 'Red', label: 'Red' },
  { value: 'White', label: 'White' },
  { value: 'Rosé', label: 'Rosé' },
  { value: 'Sparkling', label: 'Sparkling' },
  { value: 'Dessert', label: 'Dessert' },
  { value: 'Other', label: 'Other' }
];

// Helper function for safer date formatting
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'N/A';
  try {
    // First, try to clean the date string if needed
    let cleanDateStr = dateString;

    // Handle timezone issues by ensuring we're working with a clean YYYY-MM-DD format
    if (dateString.includes('T')) {
      cleanDateStr = dateString.split('T')[0];
    }

    // Try to create a valid date object
    const date = new Date(cleanDateStr);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date detected:", dateString);
      return 'Invalid Date';
    }

    // Use a more standard, unambiguous format
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return 'Invalid Date';
  }
};

// Helper function to generate a default batch ID
const generateDefaultBatchId = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `WB-${year}${month}${day}-${random}`;
};

const Batches: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';

  // Use our custom hook for batches data
  const {
    data: batches,
    loading: batchesLoading,
    error: batchesError,
    refreshData: refreshBatches,
    updateLocalData: updateBatchesLocal
  } = useFetchWithRefresh<WineBatch[]>('/api/batch', []);

  // Use our custom hook for vineyards data
  const {
    data: vineyards,
    loading: vineyardsLoading,
    error: vineyardsError
  } = useFetchWithRefresh<Vineyard[]>('/api/vineyard', []);

  // Add effect to transform batch data to ensure correct field mapping
  useEffect(() => {
    if (batches && batches.length > 0) {
      console.log("Original batch data:", batches);

      // Transform the batches to ensure all expected fields are present
      const transformedBatches = batches.map(batch => {
        // Check for raw API data structure and log it
        console.log(`Processing batch ${batch.id}:`, batch);


        // Helper function to safely convert any numeric value to string
        const formatNumberValue = (value: any): string => {
          if (value === null || value === undefined || value === 'null' || value === 'undefined') {
            return '';
          }
          return String(value);
        };

        // Create a new object with properly mapped fields
        return {
          ...batch,
          // Ensure field names are handled correctly using type assertion for flexibility
          id: batch.id,
          batch_id: (batch as any).batchId || batch.batch_id || `#${batch.id}`,
          batch_name: (batch as any).batchName || batch.batch_name || 'Unnamed Batch',
          wine_type: (batch as any).wineType || batch.wine_type || 'Unknown',
          grape_variety: (batch as any).grapeVariety || batch.grape_variety || '',
          start_date: (batch as any).startDate || batch.start_date || '',
          end_date: (batch as any).endDate || batch.end_date || null,
          status: batch.status || 'In Progress',
          // Properly format all numeric fields as strings
          weight: formatNumberValue((batch as any).weight || batch.weight),
          net_liquid_weight: formatNumberValue((batch as any).netLiquidWeight || batch.net_liquid_weight),
          final_brix: formatNumberValue((batch as any).finalBrix || batch.final_brix),
          final_ph: formatNumberValue((batch as any).finalPh || batch.final_ph),
          final_ta: formatNumberValue((batch as any).finalTa || batch.final_ta),
          final_sg: formatNumberValue((batch as any).finalSg || batch.final_sg),

          notes: batch.notes || '',
          // Ensure date fields are present
          createdAt: batch.createdAt || '',
          updatedAt: batch.updatedAt || '',
          // Other fields should already be properly mapped by our response interceptor
          vineyard: batch.vineyard,
          winemaker: batch.winemaker
        };
      });

      console.log("Transformed batch data:", transformedBatches);

      // Update the local state with properly transformed data
      updateBatchesLocal(() => transformedBatches);
    }
  }, [batches, updateBatchesLocal]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [users, setUsers] = useState<{ id: number, name: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingBatch, setEditingBatch] = useState<WineBatch | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; batchId?: number }>({ open: false });
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingFermentations, setLoadingFermentations] = useState<number[]>([]);
  const [defaultBatchId, setDefaultBatchId] = useState<string>('');

  // Merge errors from our fetch hooks
  useEffect(() => {
    setError(batchesError || vineyardsError);
  }, [batchesError, vineyardsError]);

  // Fetch users (not using the custom hook for this secondary data)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userResponse = await api.get('/api/users');
        console.log('API Response (Users):', userResponse.data);
        // Transform users to {id, name} format for dropdown
        const transformedUsers = userResponse.data.map((user: any) => ({
          id: user.id,
          name: `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.email
        }));
        setUsers(transformedUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
        // Non-critical error, don't set global error state
      }
    };

    fetchUsers();
  }, []);

  const fetchFermentationForBatch = async (batchId: number) => {
    try {
      setLoadingFermentations(prev => [...prev, batchId]);

      const response = await api.get(`/api/batch/${batchId}/fermentations`);

      // Update just this batch with fetched fermentations
      updateBatchesLocal(currentBatches =>
        currentBatches.map(batch =>
          batch.id === batchId
            ? { ...batch, fermentations: response.data }
            : batch
        )
      );

      return response.data;
    } catch (err) {
      console.error(`Error fetching fermentations for batch ${batchId}:`, err);
      return [];
    } finally {
      setLoadingFermentations(prev => prev.filter(id => id !== batchId));
    }
  };

  const toggleRowExpansion = (batchId: number) => {
    setExpandedRows(prevExpandedRows => {
      const isCurrentlyExpanded = prevExpandedRows.includes(batchId);

      // If we're expanding the row and the batch doesn't have fermentations yet, fetch them
      if (!isCurrentlyExpanded) {
        const batch = batches.find(b => b.id === batchId);
        if (batch && (!batch.fermentations || batch.fermentations.length === 0)) {
          fetchFermentationForBatch(batchId);
        }
      }

      // Toggle the expanded state
      if (isCurrentlyExpanded) {
        return prevExpandedRows.filter(id => id !== batchId);
      } else {
        return [...prevExpandedRows, batchId];
      }
    });
  };

  const handleDeleteBatch = async (batchId: number) => {
    if (!isAdmin) return;
    setConfirmDelete({ open: true, batchId });
  };

  const confirmDeleteBatch = async () => {
    if (!confirmDelete.batchId) return;
    try {
      await api.delete(`/api/batch/${confirmDelete.batchId}`);
      updateBatchesLocal(current => current.filter(b => b.id !== confirmDelete.batchId));
      setSuccessMessage('Wine batch deleted successfully');
      setConfirmDelete({ open: false });
    } catch (err: any) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.dependencies) {
        const d = data.dependencies || {};
        const parts: string[] = [];
        if (d.fermentations) parts.push(`${d.fermentations} fermentations`);
        if (d.aging) parts.push(`${d.aging} aging records`);
        if (d.bottlings) parts.push(`${d.bottlings} bottlings`);
        if (d.postFermentation) parts.push(`${d.postFermentation} post-fermentation records`);
        setError(`Cannot delete batch. Delete related ${parts.join(', ')} first.`);
      } else {
        setError(err);
      }
      setConfirmDelete({ open: false });
    }
  };

  const isRowExpanded = (batchId: number) => {
    return expandedRows.includes(batchId);
  };

  const openModal = (batch: WineBatch | null = null) => {
    setEditingBatch(batch);
    // Generate a default batch ID only when opening the modal for a new batch
    if (!batch) {
      setDefaultBatchId(generateDefaultBatchId());
    } else {
      setDefaultBatchId('');
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingBatch(null);
  };

  const handleSubmit = async (values: WineBatchFormValues, { resetForm, setSubmitting }: any) => {
    try {
      setLoading(true);

      // Debug log for form values
      console.log('Form values being submitted:', values);

      // Helper function to safely parse numbers from form values (which might be string, number, '', null, or undefined)
      const safeParseFloat = (value: any): number | null => {
        if (value === null || value === undefined || value === '') {
          return null;
        }
        const num = parseFloat(value.toString());
        return isNaN(num) ? null : num;
      };

      // Process values, ensuring correct types before sending to API
      const processedValues = {
        ...values,
        // Ensure dates are in ISO format or null/undefined based on backend expectation
        start_date: values.start_date ? new Date(values.start_date).toISOString().split('T')[0] : undefined, // Use YYYY-MM-DD or null/undefined
        end_date: values.end_date ? new Date(values.end_date).toISOString().split('T')[0] : undefined, // Use YYYY-MM-DD or null/undefined
        // Ensure IDs are numbers
        vineyard_id: values.vineyard_id ? Number(values.vineyard_id) : null,
        // Use the current user's ID for responsible_user_id
        responsible_user_id: user?.id,
        // Process measurement values
        weight: safeParseFloat(values.weight),
        net_liquid_weight: safeParseFloat(values.net_liquid_weight),
        final_brix: safeParseFloat(values.final_brix),
        final_ph: safeParseFloat(values.final_ph),
        final_ta: safeParseFloat(values.final_ta),
        final_sg: safeParseFloat(values.final_sg),

      };

      let payload: any;

      if (editingBatch) {
        // For updates, send fields allowed by PUT route (ensure snake_case)
        payload = {
          batch_name: processedValues.batch_name,
          wine_type: processedValues.wine_type,
          start_date: processedValues.start_date,
          end_date: processedValues.end_date,
          status: processedValues.status,
          vineyard_id: processedValues.vineyard_id,
          responsible_user_id: processedValues.responsible_user_id,
          notes: processedValues.notes,
          // New measurement fields
          weight: processedValues.weight,
          net_liquid_weight: processedValues.net_liquid_weight,
          final_brix: processedValues.final_brix,
          final_ph: processedValues.final_ph,
          final_ta: processedValues.final_ta,
          final_sg: processedValues.final_sg,
        };
        // Clean payload: remove undefined fields before sending
        Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

        console.log('Submitting update values:', payload);

        // Update the batch
        try {
          const response = await api.put(`/api/batch/${editingBatch.id}`, payload);
          console.log('Update response:', response.data);

          // Transform response data to ensure all fields are properly formatted
          const updatedBatch: WineBatch = {
            ...editingBatch,
            ...response.data,
            // Preserve and fix field names which might be in camelCase in the response
            vineyard_id: response.data.vineyardId || response.data.vineyard_id || editingBatch.vineyard_id,
            batch_name: response.data.batchName || response.data.batch_name || editingBatch.batch_name,
            wine_type: response.data.wineType || response.data.wine_type || editingBatch.wine_type,
            // Keep grape_variety from the existing batch - don't update it from form
            grape_variety: editingBatch.grape_variety,
            start_date: response.data.startDate || response.data.start_date || editingBatch.start_date,
            end_date: response.data.endDate || response.data.end_date || editingBatch.end_date,
            status: response.data.status || editingBatch.status,
            responsible_user_id: response.data.responsibleUserId || response.data.responsible_user_id || editingBatch.responsible_user_id,
            notes: response.data.notes ?? editingBatch.notes,
            weight: response.data.weight || editingBatch.weight,
            net_liquid_weight: response.data.netLiquidWeight || response.data.net_liquid_weight || editingBatch.net_liquid_weight,
            final_brix: response.data.finalBrix || response.data.final_brix || editingBatch.final_brix,
            final_ph: response.data.finalPh || response.data.final_ph || editingBatch.final_ph,
            final_ta: response.data.finalTa || response.data.final_ta || editingBatch.final_ta,
            final_sg: response.data.finalSg || response.data.final_sg || editingBatch.final_sg,
            // Keep date fields
            createdAt: response.data.createdAt || editingBatch.createdAt,
            updatedAt: response.data.updatedAt || new Date().toISOString(),
            // Preserve nested relationships
            vineyard: response.data.vineyard || editingBatch.vineyard,
            winemaker: response.data.winemaker || editingBatch.winemaker,
          };

          // Update the local state
          updateBatchesLocal(currentBatches =>
            currentBatches.map(b => b.id === editingBatch.id ? updatedBatch : b)
          );
          setSuccessMessage('Wine batch updated successfully!');
        } catch (error) {
          console.error('Error updating batch:', error);
          await refreshBatches(); // Fallback to refresh the data from server
          throw error;
        }
      } else {
        // For creating NEW batches, send required/allowed fields using snake_case

        // Try to get grape variety from the selected vineyard if available
        let grapeVariety = "From Vineyard"; // Default fallback
        if (processedValues.vineyard_id) {
          const selectedVineyard = vineyards.find(v => v.id === Number(processedValues.vineyard_id));
          if (selectedVineyard && selectedVineyard.planted_variety) {
            grapeVariety = selectedVineyard.planted_variety;
          }
        }

        payload = {
          batch_name: processedValues.batch_name,
          batch_id: processedValues.batch_id, // This is now required from the form
          vineyard_id: processedValues.vineyard_id,
          responsible_user_id: processedValues.responsible_user_id,
          wine_type: processedValues.wine_type,
          start_date: processedValues.start_date,
          // Get grape_variety from vineyard if available, or use placeholder
          grape_variety: grapeVariety,
          // Include status in creation payload - use default if not specified
          status: processedValues.status || 'In Progress',
          // New measurement fields
          weight: processedValues.weight,
          net_liquid_weight: processedValues.net_liquid_weight,
          final_brix: processedValues.final_brix,
          final_ph: processedValues.final_ph,
          final_ta: processedValues.final_ta,
          final_sg: processedValues.final_sg,
        };
        // Clean payload: remove undefined fields before sending
        Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

        console.log('Submitting create values:', payload);

        try {
          const response = await api.post('/api/batch', payload);
          console.log('Creation response:', response.data);

          // Transform response data to ensure all fields are properly formatted
          const newBatch: WineBatch = {
            ...response.data,
            // Fix field names which might be in camelCase in the response
            vineyard_id: response.data.vineyardId || response.data.vineyard_id,
            batch_name: response.data.batchName || response.data.batch_name,
            wine_type: response.data.wineType || response.data.wine_type,
            // Include grape_variety from API response
            grape_variety: response.data.grapeVariety || response.data.grape_variety || '',
            start_date: response.data.startDate || response.data.start_date,
            end_date: response.data.endDate || response.data.end_date,
            responsible_user_id: response.data.responsibleUserId || response.data.responsible_user_id,
            // Include all measurement fields with proper casing support
            weight: response.data.weight !== undefined ? String(response.data.weight) : '',
            net_liquid_weight: response.data.netLiquidWeight || response.data.net_liquid_weight || '',
            final_brix: response.data.finalBrix || response.data.final_brix || '',
            final_ph: response.data.finalPh || response.data.final_ph || '',
            final_ta: response.data.finalTa || response.data.final_ta || '',
            final_sg: response.data.finalSg || response.data.final_sg || '',
            notes: response.data.notes || '',
            createdAt: response.data.createdAt || new Date().toISOString(),
            updatedAt: response.data.updatedAt || new Date().toISOString(),
          };

          // Update the local state
          updateBatchesLocal(currentBatches => [...currentBatches, newBatch]);
          setSuccessMessage('Wine batch created successfully!');
        } catch (error) {
          console.error('Error creating batch:', error);
          await refreshBatches(); // Fallback to refresh the data from server
          throw error;
        }
      }

      resetForm();
      closeModal();

      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving wine batch:', err);
      setError(err);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Export columns definition for ExportButton
  const exportColumns: ExcelColumn<WineBatch>[] = [
    { header: 'Batch ID', accessor: (b) => b.batch_id },
    { header: 'Batch Name', accessor: (b) => b.batch_name },
    { header: 'Wine Type', accessor: (b) => b.wine_type || '' },
    {
      header: 'Grape Variety',
      accessor: (b) =>
        b.vineyard?.planted_variety || b.grape_variety || '',
    },
    {
      header: 'Start Date',
      accessor: (b) => b.start_date || '',
    },
    {
      header: 'Status',
      accessor: (b) => b.status,
    },
    {
      header: 'Final Brix',
      accessor: (b) => b.final_brix || '',
    },
    {
      header: 'Final pH',
      accessor: (b) => b.final_ph || '',
    },
  ];

  // Use our combined loading state
  if (batchesLoading || vineyardsLoading || loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wine Batches"
        subtitle="Track and manage your wine batches through the production process."
        actions={(
        <div className="flex gap-3">
          <ExportButton
            data={batches}
            columns={exportColumns}
            fileName="batches"
            sheetName="Batches"
          />
          <PermissionButton
            resource="batch"
            action="create"
            onClick={() => openModal()}
          >
            Add Wine Batch
          </PermissionButton>
        </div>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <Alert
          type="success"
          message={successMessage}
          autoHide
          onClose={() => setSuccessMessage(null)}
          className="mt-4"
        />
      )}

      <div className="mt-8 flex flex-col">
        {/* Desktop Table View */}
        <div className="hidden md:block -my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-table-container">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg table-enhanced">
              <table className="min-w-full divide-y divide-monday-border-light">
                <thead className="bg-monday-header">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-monday-text-muted sm:pl-6">
                      Batch ID
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text-muted">
                      Batch Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text-muted">
                      Type
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text-muted">
                      Status
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {batches.length > 0 ? (
                    batches.map((batch) => (
                      <React.Fragment key={batch.id}>
                        <tr
                          className="group border-b border-monday-border-light hover:bg-monday-hover cursor-pointer transition-colors duration-150"
                          onClick={() => toggleRowExpansion(batch.id)}
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                            <div className="flex items-center">
                              <span className="mr-2">
                                <ExpandChevron
                                  isExpanded={isRowExpanded(batch.id)}
                                  onClick={(e) => { e.stopPropagation(); toggleRowExpansion(batch.id); }}
                                />
                              </span>
                              <span className="data-row-primary">{batch.batch_id || `#${batch.id}`}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            <div className="flex flex-col">
                              <span className="data-row-primary">{batch.batch_name}</span>
                              {batch.start_date && (
                                <span className="data-row-secondary">
                                  Started: {formatDate(batch.start_date)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {batch.wine_type}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            <StatusBadge status={batch.status} fullCell />
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <TableActions>
                              <div onClick={(e) => e.stopPropagation()}>
                                <PermissionButton
                                  resource="batch"
                                  action="update"
                                  onClick={() => openModal(batch)}
                                  variant="secondary"
                                  className="!px-2.5 !py-1 !text-sm !bg-transparent !text-monday-text-muted hover:!bg-neutral-100 hover:!text-monday-text !border-0 !shadow-none"
                                >
                                  Edit
                                </PermissionButton>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteBatch(batch.id); }}
                                  className="px-2.5 py-1 text-sm font-medium text-monday-text-muted hover:text-error-500 hover:bg-error-50 rounded-md transition-colors"
                                  title="Delete batch"
                                >
                                  Delete
                                </button>
                              )}
                            </TableActions>
                          </td>
                        </tr>
                        <ExpandableRow isExpanded={isRowExpanded(batch.id)} colSpan={8}>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

                                <div className="flex flex-col">
                                  <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Grape Variety</span>
                                  <span className="text-sm text-monday-text-muted">{batch.vineyard?.planted_variety || batch.grape_variety || "N/A"}</span>
                                </div>
                                {batch.vineyard && (
                                  <div className="flex flex-col">
                                    <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Vineyard</span>
                                    <span className="text-sm text-monday-text-muted">{batch.vineyard.name}</span>
                                  </div>
                                )}
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Final Brix</span>
                                  <span className="text-sm text-monday-text-muted">{batch.final_brix || "N/A"}</span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Final pH</span>
                                  <span className="text-sm text-monday-text-muted">{batch.final_ph || "N/A"}</span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Final TA</span>
                                  <span className="text-sm text-monday-text-muted">{batch.final_ta || "N/A"}</span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Final SG</span>
                                  <span className="text-sm text-monday-text-muted">{batch.final_sg || "N/A"}</span>
                                </div>

                                {/* Vineyard Information */}
                                {batch.vineyard && (
                                  <>
                                    <div className="flex flex-col">
                                      <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Vineyard Location</span>
                                      <span className="text-sm text-monday-text-muted">{batch.vineyard.location || "N/A"}</span>
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Vineyard Notes</span>
                                      <span className="text-sm text-monday-text-muted">{batch.vineyard.notes || "N/A"}</span>
                                    </div>
                                  </>
                                )}

                                {/* Winemaker Information */}
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Last Updated</span>
                                  <span className="text-sm text-monday-text-muted">
                                    {batch.updatedAt
                                      ? formatDate(batch.updatedAt)
                                      : "N/A"}
                                  </span>
                                </div>

                                {/* Additional batch measurements */}
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Net Liquid Weight</span>
                                  <span className="text-sm text-monday-text-muted">{batch.net_liquid_weight || "N/A"}</span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium uppercase tracking-wide text-monday-text-muted">Notes</span>
                                  <span className="text-sm text-monday-text-muted">{batch.notes || "N/A"}</span>
                                </div>
                              </div>
                        </ExpandableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="py-4 text-center text-sm text-monday-text-muted">
                        No wine batches found. Click "Add Wine Batch" to create your first batch.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Mobile Card/List View */}
        <div className="md:hidden space-y-4">
          {batches.length > 0 ? (
            batches.map((batch) => (
              <div key={batch.id} className="bg-white/95 rounded-xl shadow border border-monday-border-light p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-bold text-monday-text">{batch.batch_name}</div>
                    <div className="text-xs text-monday-text-muted">Batch ID: {batch.batch_id}</div>
                    <div className="text-sm text-monday-text-muted">{batch.wine_type}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold text-white min-w-[80px] text-center ${batch.status === 'Completed' ? 'bg-success-500' : batch.status === 'In Progress' ? 'bg-info-500' : 'bg-warning-500'}`}>{batch.status}</span>
                    <PermissionButton
                      resource="batch"
                      action="update"
                      onClick={() => openModal(batch)}
                      variant="secondary"
                      className="!px-3 !py-1 !text-sm"
                    >
                      Edit
                    </PermissionButton>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 mt-2">
                  {batch.vineyard && (
                    <div>
                      <span className="block text-xs text-monday-text-muted">Vineyard</span>
                      <span className="text-sm text-neutral-700">{batch.vineyard.name}</span>
                    </div>
                  )}
                  <div>
                    <span className="block text-xs text-monday-text-muted">Grape Variety</span>
                    <span className="text-sm text-neutral-700">{batch.vineyard?.planted_variety || batch.grape_variety || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">Started</span>
                    <span className="text-sm text-neutral-700">{batch.start_date ? formatDate(batch.start_date) : 'N/A'}</span>
                  </div>
                </div>
                {/* Expandable details for more info if needed */}
                {/* Add more fields as needed */}
              </div>
            ))
          ) : (
            <div className="bg-white/95 rounded-xl shadow border border-monday-border-light p-6 text-center text-monday-text-muted">
              <EmptyState
                title="No wine batches found"
                message='Click "Add Wine Batch" to create your first batch.'
                action={{
                  label: 'Add Wine Batch',
                  onClick: () => openModal(),
                }}
              />
            </div>
          )}
        </div>
      </div>

      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingBatch ? 'Edit Wine Batch' : 'Add New Wine Batch'} size="full">
        <div className="mobile-form-container">
          <Formik
            initialValues={{
              batch_name: editingBatch?.batch_name ?? '',
              batch_id: editingBatch?.batch_id ?? defaultBatchId,
              vineyard_id: editingBatch?.vineyard_id ?? undefined,
              responsible_user_id: editingBatch?.responsible_user_id ?? undefined,
              wine_type: editingBatch?.wine_type ?? '',
              start_date: editingBatch?.start_date ? editingBatch.start_date.split('T')[0] : '',
              end_date: editingBatch?.end_date ? editingBatch.end_date.split('T')[0] : '',
              status: editingBatch?.status ?? 'In Progress',
              notes: editingBatch?.notes ?? '',
              // Measurement values
              weight: editingBatch?.weight ? Number(editingBatch.weight) : undefined,
              net_liquid_weight: editingBatch?.net_liquid_weight ? Number(editingBatch.net_liquid_weight) : undefined,
              final_brix: editingBatch?.final_brix ? Number(editingBatch.final_brix) : undefined,
              final_ph: editingBatch?.final_ph ? Number(editingBatch.final_ph) : undefined,
              final_ta: editingBatch?.final_ta ? Number(editingBatch.final_ta) : undefined,
              final_sg: editingBatch?.final_sg ? Number(editingBatch.final_sg) : undefined,
            }}
            validationSchema={WineBatchSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting }) => (
              <Form className="space-y-4">
                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <FormField name="batch_name" label="Batch Name" type="text" required />
                  <FormField name="batch_id" label="External Batch ID" type="text" placeholder="e.g., 2024-XYZ-01" required />
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <FormSelect
                    name="vineyard_id"
                    label="Vineyard"
                    options={[
                      { value: '', label: 'Select Vineyard' },
                      ...vineyards.map((vineyard) => ({
                        value: vineyard.id,
                        label: vineyard.name,
                      })),
                    ]}
                  />
                  <FormSelect
                    name="wine_type"
                    label="Wine Type"
                    required
                    options={[
                      { value: '', label: 'Select Wine Type' },
                      ...wineTypes.map((type) => ({
                        value: type.value,
                        label: type.label,
                      })),
                    ]}
                  />
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <FormDateInput name="start_date" label="Start Date" required />
                  <FormDateInput name="end_date" label="End Date" />
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <FormSelect
                    name="status"
                    label="Status"
                    options={statusOptions.map((option) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                  />
                </div>

                {/* Measurement Section */}
                <div className="form-section-title">
                  Measurement Values
                  <span className="block font-normal text-xs text-monday-text-muted mt-0.5">Optional measurements for the wine batch</span>
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <FormField name="weight" label="Weight" type="number" placeholder="Weight in kg" />
                  <FormField name="net_liquid_weight" label="Net Liquid Weight" type="number" placeholder="Net weight in kg" />
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-4">
                  <FormField name="final_brix" label="Final Brix" type="number" placeholder="e.g., 24.5" step="0.1" />
                  <FormField name="final_ph" label="Final pH" type="number" placeholder="e.g., 3.5" step="0.01" />
                  <FormField name="final_ta" label="Final TA" type="number" placeholder="e.g., 0.65" step="0.01" />
                  <FormField name="final_sg" label="Final SG" type="number" placeholder="e.g., 0.995" step="0.001" />
                </div>

                <FormTextarea
                  name="notes"
                  label="Notes"
                  rows={3}
                  placeholder="Add any relevant notes about this batch"
                />

                <FormActions
                  onCancel={closeModal}
                  isSubmitting={isSubmitting}
                  submitText={editingBatch ? 'Update Batch' : 'Create Batch'}
                />
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>

      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        onConfirm={confirmDeleteBatch}
        title="Delete Wine Batch"
        message="Are you sure you want to delete this wine batch? This action cannot be undone."
        variant="danger"
      />
    </div>
  );
};

export default Batches;
